import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Group,
  rem,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { randomId } from "@mantine/hooks";
import {
  IconCheck,
  IconExclamationCircle,
  IconMinus,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { sortBy } from "lodash-es";
import { z } from "zod";

import { useCopyBookinginbulk } from "@/shared/api/generated/booking";
import { useGetCustomers } from "@/shared/api/generated/customers";
import {
  CopyBookingRequest,
  GetBookingsItem,
} from "@/shared/api/generated/schemas";
import { MaskedDateInput } from "@/shared/components/MaskedDateInput";
import { nullableInput } from "@/shared/utils/forms/nullableInput";

function useFormSchema() {
  const { t } = useLingui();

  return z.object({
    bookings: z.array(
      z.object({
        clientRef: z.string(),
        state: z.enum(["draft", "success", "failure"]),
        serverError: z.string().optional(),
        customer: nullableInput(
          z.string().min(1, { message: t`Velg kunde` }),
          t`Velg kunde`,
        ),
        groupName: z.string(),
        date: nullableInput(
          z.date({ message: t`Velg en dato` }),
          t`Velg en dato`,
        ),
        timeStart: z.string(),
        timeEnd: z.string(),
      }),
    ),
  });
}

type DuplicateBookingFormInitial = z.input<ReturnType<typeof useFormSchema>>;
type DuplicateBookingFormSubmit = z.output<ReturnType<typeof useFormSchema>>;

function getDefaultBooking(
  booking: GetBookingsItem,
): DuplicateBookingFormInitial["bookings"][0] {
  return {
    clientRef: randomId(),
    state: "draft",
    serverError: undefined,
    customer: booking.customer.id.toString(),
    groupName: booking.group?.name ?? "",
    date: dayjs(booking.arrival).toDate(),
    timeStart: dayjs(booking.arrival).format("HH:mm"),
    timeEnd: dayjs(booking.departure).format("HH:mm"),
  };
}

export default function DuplicateBookingForm({
  onClose,
  booking,
}: {
  onClose: () => void;
  booking: GetBookingsItem;
}) {
  const formSchema = useFormSchema();

  const form = useForm<
    DuplicateBookingFormInitial,
    (v: DuplicateBookingFormInitial) => DuplicateBookingFormSubmit
  >({
    mode: "controlled",
    initialValues: {
      bookings: [getDefaultBooking(booking)],
    },
    validate: zodResolver(formSchema),
  });

  const {
    data: copyBookinginbulkData,
    mutateAsync: copyBookinginbulk,
    isPending: isCopyBookinginbulkPending,
    isSuccess: isCopyBookinginbulkSuccess,
    isIdle: isCopyBookinginbulkIdle,
  } = useCopyBookinginbulk();

  const hasFailuredBookings =
    copyBookinginbulkData?.data?.failure &&
    copyBookinginbulkData.data.failure.length > 0;

  async function handleSubmit(values: DuplicateBookingFormSubmit) {
    const draftOrFailedBookings = values.bookings.filter(
      (booking) => booking.state !== "success",
    );

    const newBookings = draftOrFailedBookings.map((newBooking) => {
      let arrival = dayjs(newBooking.date).startOf("day");
      let departure = dayjs(newBooking.date).endOf("day");

      if (newBooking.timeStart) {
        const startTime = dayjs(newBooking.timeStart, "HH:mm");
        arrival = dayjs(newBooking.date)
          .hour(startTime.hour())
          .minute(startTime.minute());
      }

      if (newBooking.timeEnd) {
        const endTime = dayjs(newBooking.timeEnd, "HH:mm");
        departure = dayjs(newBooking.date)
          .hour(endTime.hour())
          .minute(endTime.minute());
      }

      return {
        clientRef: newBooking.clientRef,
        arrival: arrival.toISOString(),
        departure: departure?.toISOString(),
        customerId: Number(newBooking.customer),
        groupName: newBooking.groupName,
      } satisfies CopyBookingRequest;
    });

    await copyBookinginbulk(
      {
        id: booking.id,
        data: { copyDetails: newBookings },
      },
      {
        onSuccess: (response) => {
          const errorInput = document.querySelector(
            `[data-path="copy-booking-bulk-error"]`,
          );
          errorInput?.scrollIntoView({ behavior: "smooth", block: "center" });

          const failure = response.data.failure;
          const success = response.data.success;

          const bookingsAfterError = values.bookings.map((booking) => {
            const successBooking = success?.find(
              (successBooking) =>
                successBooking.clientRef === booking.clientRef,
            );
            const failedBooking = failure?.find(
              (failedBooking) => failedBooking.clientRef === booking.clientRef,
            );

            if (successBooking) {
              return {
                ...booking,
                state: "success" as const,
                serverError: undefined,
              };
            }

            if (failedBooking) {
              return {
                ...booking,
                state: "failure" as const,
                serverError: failedBooking.error,
              };
            }

            return { ...booking, serverError: undefined };
          });

          form.setFieldValue(
            "bookings",
            bookingsAfterError satisfies DuplicateBookingFormInitial["bookings"],
          );
        },
      },
    );
  }

  function addBooking() {
    form.insertListItem(
      "bookings",
      getDefaultBooking(
        booking,
      ) satisfies DuplicateBookingFormInitial["bookings"][0],
    );
  }

  const { data: customers } = useGetCustomers();

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xs">
        <Text c="dimmed" size="sm">
          <Trans>
            Opprett en eller flere kopier av denne bookingen. Kopiene vil bli
            tilgjengelige i bookingoversikten.
          </Trans>
        </Text>

        <Box data-path="copy-booking-bulk-error">
          {hasFailuredBookings && (
            <Alert
              color="red"
              title={<Trans>Kopiering mislyktes</Trans>}
              icon={<IconExclamationCircle />}
            >
              <Trans>
                En eller flere kopier av denne bookingen kunne ikke opprettes.
                De mislykkede kopiene er markert med &apos;X&apos; i tabellen
                nedenfor. Du kan forsøke igjen, eller lukke dialogen. Vellykkede
                kopier vil vises i bookinglisten.
              </Trans>
            </Alert>
          )}
          {isCopyBookinginbulkSuccess && !hasFailuredBookings && (
            <Alert
              color="green"
              title={<Trans>Kopiering vellykket</Trans>}
              icon={<IconCheck />}
            />
          )}
        </Box>

        <Table withRowBorders={false} horizontalSpacing={4} verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th></Table.Th>
              <Table.Th>
                <Trans>Kunde</Trans>
              </Table.Th>
              <Table.Th>
                <Trans>Gruppe</Trans>
              </Table.Th>
              <Table.Th style={{ width: rem(130) }}>
                <Trans>Ankomstdato</Trans>
              </Table.Th>
              <Table.Th>
                <Trans>Ankomst</Trans>
              </Table.Th>
              <Table.Th>
                <Trans>Avreise</Trans>
              </Table.Th>
              <Table.Th style={{ width: rem(36) }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {form.getValues().bookings.map((newBooking, index) => (
              <>
                <Table.Tr key={newBooking.clientRef}>
                  <Table.Td valign="middle">
                    {newBooking.state === "success" ? (
                      <IconCheck
                        display="block"
                        size={20}
                        color="var(--mantine-color-green-outline)"
                      />
                    ) : newBooking.state === "failure" ? (
                      <IconX
                        display="block"
                        size={20}
                        color="var(--mantine-color-red-outline)"
                      />
                    ) : (
                      index + 1
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Select
                      data={sortBy(
                        customers?.data?.map((customer) => ({
                          value: customer.id.toString(),
                          label: customer.name,
                        })),
                        (o) => o.label.toLowerCase(),
                      )}
                      searchable
                      clearable
                      {...form.getInputProps(`bookings.${index}.customer`)}
                      key={form.key(`bookings.${index}.customer`)}
                      disabled={newBooking.state === "success"}
                      error={
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        form.getInputProps(`bookings.${index}.customer`)
                          .error || newBooking.state === "failure"
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      {...form.getInputProps(`bookings.${index}.groupName`)}
                      key={form.key(`bookings.${index}.groupName`)}
                      disabled={newBooking.state === "success"}
                      error={
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        form.getInputProps(`bookings.${index}.groupName`)
                          .error || newBooking.state === "failure"
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <MaskedDateInput
                      clearable={newBooking.state !== "success"}
                      {...form.getInputProps(`bookings.${index}.date`)}
                      key={form.key(`bookings.${index}.date`)}
                      disabled={newBooking.state === "success"}
                      error={
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        form.getInputProps(`bookings.${index}.date`).error ||
                        newBooking.state === "failure"
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TimeInput
                      {...form.getInputProps(`bookings.${index}.timeStart`)}
                      key={form.key(`bookings.${index}.timeStart`)}
                      disabled={newBooking.state === "success"}
                      error={
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        form.getInputProps(`bookings.${index}.timeStart`)
                          .error || newBooking.state === "failure"
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TimeInput
                      {...form.getInputProps(`bookings.${index}.timeEnd`)}
                      key={form.key(`bookings.${index}.timeEnd`)}
                      disabled={newBooking.state === "success"}
                      error={
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        form.getInputProps(`bookings.${index}.timeEnd`).error ||
                        newBooking.state === "failure"
                      }
                    />
                  </Table.Td>
                  <Table.Td valign="top">
                    {newBooking.state !== "success" && (
                      <ActionIcon
                        my={4}
                        onClick={() => {
                          form.removeListItem("bookings", index);
                        }}
                        variant="outline"
                        color="red"
                      >
                        <IconMinus />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
                {newBooking.state === "failure" && (
                  <Table.Tr key={`${newBooking.clientRef}-serverError`}>
                    <Table.Td></Table.Td>
                    <Table.Td colSpan={5}>
                      <Text size="xs" c="red">
                        {newBooking.serverError}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </>
            ))}
          </Table.Tbody>
        </Table>

        {isCopyBookinginbulkIdle && (
          <Group>
            <Button
              size="compact-sm"
              leftSection={<IconPlus size={20} />}
              variant="outline"
              onClick={() => {
                for (let i = 0; i < 10; i++) {
                  addBooking();
                }
              }}
            >
              <Trans>10 kopier</Trans>
            </Button>
            <Button
              size="compact-sm"
              leftSection={<IconPlus size={20} />}
              variant="outline"
              onClick={addBooking}
            >
              <Trans>1 kopi</Trans>
            </Button>
          </Group>
        )}

        <Group justify="end" gap={8}>
          <Button variant="subtle" onClick={onClose}>
            <Trans>Lukk</Trans>
          </Button>
          {!(isCopyBookinginbulkSuccess && !hasFailuredBookings) && (
            <Button type="submit" loading={isCopyBookinginbulkPending}>
              {hasFailuredBookings ? (
                <Trans>Prøv igjen</Trans>
              ) : (
                <Trans>Lagre</Trans>
              )}
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
}
